import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import './MediaViewer.css';
import { IconX, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

export default function MediaViewer({ mediaUrls, mediaType, onClose, initialIndex = 0 }) {
    // Setup body scroll lock (global lock counter)
    useLayoutEffect(() => {
        const w = window;
        w.__mv_locks = (w.__mv_locks || 0) + 1;
        if (w.__mv_locks === 1) {
            w.__mv_prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            document.body.classList.add('media-viewer-open');
        }

        return () => {
            const ww = window;
            ww.__mv_locks = Math.max(0, (ww.__mv_locks || 0) - 1);
            if (ww.__mv_locks === 0) {
                document.body.style.overflow = ww.__mv_prevOverflow || '';
                document.body.classList.remove('media-viewer-open');
                try { delete ww.__mv_prevOverflow; } catch {}
            }
        };
    }, []);
    const clampIndex = (idx) => {
        const len = Array.isArray(mediaUrls) ? mediaUrls.length : 0;
        if (len <= 0) return 0;
        const n = Number.isFinite(idx) ? Math.max(0, Math.min(len - 1, Math.floor(idx))) : 0;
        return n;
    };

    const [currentIndex, setCurrentIndex] = useState(clampIndex(initialIndex));
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const stageRef = useRef(null);
    const imgRef = useRef(null);

    // Zoom/Pan state
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const [dragging, setDragging] = useState(false);
    const lastPos = useRef({ x: 0, y: 0 });
    const pinchStart = useRef({ dist: 0, scaleAtStart: 1 });
    const dragMovedRef = useRef(false);

    // Reset transforms when image index changes
    useEffect(() => {
        setScale(1);
        setTx(0);
        setTy(0);
        setDragging(false);
        if (imgRef.current) imgRef.current.classList.remove('grabbing');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);

    useEffect(() => {
        setCurrentIndex(clampIndex(initialIndex));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialIndex, mediaUrls?.length]);
    const hasMultiple = mediaUrls.length > 1;

    const handlePrevious = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => (prev - 1 + mediaUrls.length) % mediaUrls.length);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => (prev + 1) % mediaUrls.length);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowLeft' && hasMultiple) {
            handlePrevious(e);
        } else if (e.key === 'ArrowRight' && hasMultiple) {
            handleNext(e);
        }
    };

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            // Pinch start (always allow)
            const [t1, t2] = e.touches;
            const dx = t1.clientX - t2.clientX;
            const dy = t1.clientY - t2.clientY;
            pinchStart.current = { dist: Math.hypot(dx, dy), scaleAtStart: scale };
            return;
        }
        if (scale > 1) return; // don't start swipe when zoomed (pan handles movement)
        if (e.touches.length === 1 && hasMultiple) {
            const t = e.touches[0];
            touchStartX.current = t.clientX;
            touchStartY.current = t.clientY;
        }
    };

    const onTouchEnd = (e) => {
        if (scale > 1) return; // don't swipe when zoomed
        if (e.touches.length === 0 && e.changedTouches.length === 1 && hasMultiple) {
            const t = e.changedTouches[0];
            const dx = t.clientX - (touchStartX.current ?? t.clientX);
            const dy = t.clientY - (touchStartY.current ?? t.clientY);
            if (Math.abs(dx) > 40 && Math.abs(dy) < 60) {
                if (dx < 0) {
                    setCurrentIndex(prev => (prev + 1) % mediaUrls.length);
                } else {
                    setCurrentIndex(prev => (prev - 1 + mediaUrls.length) % mediaUrls.length);
                }
            }
            touchStartX.current = null;
            touchStartY.current = null;
        }
    };

    // Pinch zoom handler
    const onTouchMove = (e) => {
        if (e.touches.length === 2) {
            e.preventDefault();
            const [t1, t2] = e.touches;
            const dx = t1.clientX - t2.clientX;
            const dy = t1.clientY - t2.clientY;
            const dist = Math.hypot(dx, dy);
            const start = pinchStart.current;
            if (start.dist > 0) {
                const nextScale = Math.max(1, Math.min(4, (dist / start.dist) * start.scaleAtStart));
                setScale(nextScale);
            }
        }
    };

    // Desktop/mouse interactions for pan/zoom
    const onPointerDown = (e) => {
        // Allow pan only when zoomed in
        if (scale <= 1) return;
        if (e.target === imgRef.current) {
            lastPos.current = { x: e.clientX, y: e.clientY };
            setDragging(true);
            dragMovedRef.current = false;
            try { e.target.setPointerCapture(e.pointerId); } catch {}
            imgRef.current.classList.add('grabbing');
        }
    };
    const onPointerMove = (e) => {
        if (!dragging) return;
        const dx = e.clientX - lastPos.current.x;
        const dy = e.clientY - lastPos.current.y;
        lastPos.current = { x: e.clientX, y: e.clientY };
        const nextX = tx + dx;
        const nextY = ty + dy;
        const { x, y } = clampPan(nextX, nextY);
        setTx(x);
        setTy(y);
        if (!dragMovedRef.current && (Math.abs(dx) + Math.abs(dy) > 3)) {
            dragMovedRef.current = true;
        }
    };
    const onPointerUp = (e) => {
        if (!dragging) return;
        setDragging(false);
        try { e.target.releasePointerCapture(e.pointerId); } catch {}
        imgRef.current && imgRef.current.classList.remove('grabbing');
    };

    const onPointerCancel = (e) => {
        if (!dragging) return;
        setDragging(false);
        try { e.target.releasePointerCapture?.(e.pointerId); } catch {}
        imgRef.current && imgRef.current.classList.remove('grabbing');
    };

    const onPointerLeave = () => {
        if (!dragging) return;
        setDragging(false);
        imgRef.current && imgRef.current.classList.remove('grabbing');
    };

    // Safety: end drag if pointerup happens outside the image
    useEffect(() => {
        const handleWindowPointerUp = () => {
            if (dragging) {
                setDragging(false);
                imgRef.current && imgRef.current.classList.remove('grabbing');
            }
        };
        window.addEventListener('pointerup', handleWindowPointerUp);
        window.addEventListener('blur', handleWindowPointerUp);
        return () => {
            window.removeEventListener('pointerup', handleWindowPointerUp);
            window.removeEventListener('blur', handleWindowPointerUp);
        };
    }, [dragging]);

    // Wheel zoom centered roughly around stage center
    const onWheel = (e) => {
        if (mediaType !== 'image') return;
        e.preventDefault();
        const delta = -e.deltaY;
        const factor = delta > 0 ? 1.1 : 0.9;
        setScale(prev => {
            const next = Math.max(1, Math.min(4, prev * factor));
            if (next === 1) { setTx(0); setTy(0); }
            return next;
        });
    };

    const onDoubleClick = (e) => {
        if (mediaType !== 'image') return;
        e.preventDefault();
        setScale(prev => {
            const next = prev > 1 ? 1 : 2;
            if (next === 1) { setTx(0); setTy(0); }
            return next;
        });
    };

    // Clamp pan based on current scaled image and stage size
    const clampPan = (nx, ny) => {
        const stage = stageRef.current;
        const img = imgRef.current;
        if (!stage || !img) return { x: nx, y: ny };
        const stageRect = stage.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect(); // width/height reflect current scale
        const maxX = Math.max(0, (imgRect.width - stageRect.width) / 2);
        const maxY = Math.max(0, (imgRect.height - stageRect.height) / 2);
        return {
            x: Math.max(-maxX, Math.min(maxX, nx)),
            y: Math.max(-maxY, Math.min(maxY, ny))
        };
    };

    // Re-clamp translation when scale changes
    useEffect(() => {
        if (scale <= 1) { setTx(0); setTy(0); return; }
        const { x, y } = clampPan(tx, ty);
        if (x !== tx) setTx(x);
        if (y !== ty) setTy(y);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale]);

    const handleBackdropClick = () => {
        // Prevent close if the last interaction was a drag gesture
        if (dragMovedRef.current) {
            dragMovedRef.current = false;
            return;
        }
        onClose();
    };

    const overlay = (
        <div 
            className="media-viewer"
            onClick={handleBackdropClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="dialog"
            aria-modal="true"
        >
            <button className="media-viewer__close" onClick={onClose}>
                <IconX size={24} />
            </button>

            <div className="media-viewer__content" onClick={e => e.stopPropagation()} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchMove={onTouchMove} onWheel={onWheel} onDoubleClick={onDoubleClick}>
                {mediaType === 'image' ? (
                    <div className="media-viewer__stage" ref={stageRef}>
                        <img 
                            ref={imgRef}
                            src={mediaUrls[currentIndex]} 
                            alt={`Media ${currentIndex + 1}`}
                            className={`media-viewer__image ${scale > 1 ? 'is-zoomed' : ''}`}
                            style={{ transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})` }}
                            onPointerDown={onPointerDown}
                            onPointerMove={onPointerMove}
                            onPointerUp={onPointerUp}
                            onPointerCancel={onPointerCancel}
                            onPointerLeave={onPointerLeave}
                            draggable={false}
                            onDragStart={(e) => e.preventDefault()}
                        />
                    </div>
                ) : (
                    <video
                        src={mediaUrls[currentIndex]}
                        controls
                        autoPlay
                        className="media-viewer__video"
                    />
                )}

                {hasMultiple && (
                    <>
                        <button 
                            className="media-viewer__nav media-viewer__nav--prev"
                            onClick={handlePrevious}
                            aria-label="Previous media"
                        >
                            <IconChevronLeft size={24} />
                        </button>
                        <button 
                            className="media-viewer__nav media-viewer__nav--next"
                            onClick={handleNext}
                            aria-label="Next media"
                        >
                            <IconChevronRight size={24} />
                        </button>
                        <div className="media-viewer__counter">
                            {currentIndex + 1} / {mediaUrls.length}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return ReactDOM.createPortal(overlay, document.body);
}